import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Image,
  Segment,
  Header,
  Form,
  Button,
  Icon,
  Divider,
  Card,
  Rating,
  Label,
  Tab,
  Message,
  List,
  TextArea,
  Statistic,
  Popup
} from 'semantic-ui-react';
import {
  fetchActorUser,
  updateActorUser,
  selectActorUsers,
  selectActorUsersStatus,
  selectActorUsersError
} from '../../slices/voiceActorSlice';
import {
  fetchUserProfiles,
  selectUserProfiles
} from '../../slices/userRoleSlice';
import {
  fetchPrimaryPortfolio,
  fetchSampleCharacters,
  selectPrimaryPortfolio,
  selectSampleCharacters,
  selectLoadingStates as selectSampleLoadingStates
} from '../../slices/sampleCharacterSlice';
import {
  fetchActorApplications,
  selectActorApplications
} from '../../slices/applicationSlice';
import { AuthStdFooter } from "../../utils/auth-std-footer";
import useSignedAvatar from '../../hooks/useSignedAvatar';
import portfolioImage from '../../assets/shutterstock_2294366549-480x480.png';
import '../../styles/landing-pages.css';
import '../../styles/article.css';

// Import icons for statistics and quick actions
import reviewIcon from '../../assets/review-quotes.svg';

import profileIcon from '../../assets/profileLineGlasses.svg';
import myProfileLine from '../../assets/myProfileLine.svg';
import lightening from '../../assets/lightening.svg';
import microphoneIcon from '../../assets/shutterstock_2294366549-480x480.png'; // Placeholder for microphone icon
import myRatingIcon from '../../assets/rating-icon.svg';

const MyPortfolio = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentUser = useSelector(state => state.user.profile?.data);
  const actorId = currentUser?.userId;
  // Get VoiceActorProfile from userRoleSlice (same source as /profile page)
  const userProfiles = useSelector(selectUserProfiles);
  const voiceActorProfile = userProfiles?.find(p =>
    p.profileType === 'VoiceActorProfile' || p.type === 'voiceActor'
  ) || null;

  // Also check voiceActorSlice for backward compat
  const actorUsers = useSelector(selectActorUsers);
  const actorUserFromSlice = actorUsers?.find(a => a.userId === actorId || a.uuid === actorId) || null;

  // Use voiceActorProfile (from userRoleSlice) as primary, fall back to voiceActorSlice
  const actorUser = voiceActorProfile || actorUserFromSlice;

  const status = useSelector(selectActorUsersStatus);
  const error = useSelector(selectActorUsersError);

  // Sign the S3 profile image URL
  const { signedUrl: signedProfileImageUrl } = useSignedAvatar(
    actorUser?.profilePictureUrl,
    actorId,
    portfolioImage
  );

  // Refs for tab navigation
  const tabsRef = useRef(null);
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  // Local state for form data
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    description: '',
    street: '',
    city: '',
    state: '',
    zipcode: '',
    country: '',
    phones: [],
    hobbies: [],
    skills: [],
    specialties: [],
    profilePictureUrl: ''
  });

  // Local state for new items
  const [newPhone, setNewPhone] = useState('');
  const [newHobby, setNewHobby] = useState('');
  const [newSkill, setNewSkill] = useState('');
  const [newSpecialty, setNewSpecialty] = useState('');

  // Local state for edit mode
  const [isEditing, setIsEditing] = useState(false);

  // Production data from Redux
  const primaryPortfolio = useSelector(selectPrimaryPortfolio);
  const sampleCharacters = useSelector(selectSampleCharacters);
  const sampleLoading = useSelector(selectSampleLoadingStates);
  const actorApplications = useSelector(selectActorApplications(actorId));

  // Calculate portfolio statistics using production data
  // Use actorUser if available, otherwise fall back to currentUser
  const profileSource = actorUser || currentUser;

  const calculatePortfolioStats = () => {
    const activeApps = (actorApplications || []).filter(a =>
      ['pending', 'submitted', 'under_review', 'in_review'].includes(a.status?.toLowerCase())
    );

    // Calculate profile completion percentage using whichever profile source is available
    const requiredFields = [
      'firstName', 'lastName', 'description', 'city', 'state',
      'profilePictureUrl', 'skills', 'specialties'
    ];

    let profileCompletion = 0;
    if (profileSource) {
      const completedFields = requiredFields.filter(field => {
        const value = profileSource[field];
        if (Array.isArray(value)) return value.length > 0;
        return value && value.toString().trim() !== '';
      });
      profileCompletion = Math.round((completedFields.length / requiredFields.length) * 100);
    }

    return {
      voiceSamples: sampleCharacters.length,
      activeApplications: activeApps.length,
      reviews: (actorUser?.reviewCount || 0),
      averageRating: parseFloat(actorUser?.rating) || 0,
      profileCompletion
    };
  };

  // Handle quick action clicks
  const handleQuickAction = (action) => {
    switch (action) {
      case 'samples':
        setActiveTabIndex(0);
        if (tabsRef.current) {
          tabsRef.current.scrollIntoView({ behavior: 'smooth' });
        }
        break;
      case 'reviews':
        setActiveTabIndex(1);
        if (tabsRef.current) {
          tabsRef.current.scrollIntoView({ behavior: 'smooth' });
        }
        break;
      default:
        break;
    }
  };

  // Get actor name and type for display
  const actorName = actorUser ? `${actorUser.firstName} ${actorUser.lastName}` : 'Voice Actor';
  const actorType = actorUser?.actorType || 'Voice Actor';
  const portfolioStats = calculatePortfolioStats();

  // Use actorUser if available, otherwise fall back to currentUser (from user.profile.data)
  const profileData = actorUser || currentUser;

  // Compute missing profile fields for the popup
  const getMissingFields = () => {
    if (!profileData) return ['All profile fields (no profile loaded)'];

    const fieldLabels = {
      firstName: 'First Name',
      lastName: 'Last Name',
      description: 'Description',
      city: 'City',
      state: 'State',
      profilePictureUrl: 'Profile Picture',
      skills: 'Skills',
      specialties: 'Specialties'
    };

    const requiredFields = Object.keys(fieldLabels);
    return requiredFields
      .filter(field => {
        const value = profileData[field];
        if (Array.isArray(value)) return value.length === 0;
        return !value || value.toString().trim() === '';
      })
      .map(field => fieldLabels[field]);
  };

  const missingFields = getMissingFields();

  // Quick actions configuration
  const quickActions = [
    // {
    //   icon: 'user',
    //   img: profileIcon,
    //   title: 'Manage Profile',
    //   description: 'Edit your voice actor profile',
    //   link: '/profile'
    // },
    {
      icon: 'microphone',
      img: microphoneIcon,
      title: 'Record Samples',
      description: 'Add new voice samples',
      action: () => navigate('/dashboard', { state: { menuSelection: 'VOICE-SAMPLE-WORKSHEET' } })
    },
    {
      icon: 'star',
      img: reviewIcon,
      title: 'View Reviews',
      description: 'See your client reviews',
      action: () => handleQuickAction('reviews')
    },
    // {
    //   icon: 'search',
    //   img: lightening,
    //   title: 'Browse Opportunities',
    //   description: 'Find new voice acting jobs',
    //   link: '/opportunities'
    // }
  ];

  // Statistics for the overview section
  const statistics = [
    {
      label: 'Voice Samples',
      value: portfolioStats.voiceSamples.toString(),
      icon: 'microphone',
      img: microphoneIcon
    },
    {
      label: 'Active Applications',
      value: portfolioStats.activeApplications.toString(),
      icon: 'file',
      img: profileIcon
    },
    {
      label: 'Reviews',
      value: portfolioStats.reviews.toString(),
      icon: 'star',
      img: reviewIcon
    },
    {
      label: 'Average Rating',
      value: portfolioStats.averageRating.toFixed(1),
      icon: 'star',
      img: myRatingIcon
    },
    {
      label: portfolioStats.profileCompletion === 100 ? 'Profile Complete' : 'Profile Required',
      value: portfolioStats.profileCompletion === 100 ? '' : '!',
      icon: portfolioStats.profileCompletion === 100 ? 'check circle' : 'warning sign',
      img: profileIcon,
      link: '/profile',
      color: portfolioStats.profileCompletion === 100 ? 'green' : 'orange'
    }
  ];

  // Update local state when actor data is loaded
  useEffect(() => {
    if (actorUser) {
      setFormData({
        firstName: actorUser.firstName || '',
        lastName: actorUser.lastName || '',
        description: actorUser.description || '',
        street: actorUser.street || '',
        city: actorUser.city || '',
        state: actorUser.state || '',
        zipcode: actorUser.zipcode || '',
        country: actorUser.country || '',
        phones: actorUser.phones || [],
        hobbies: actorUser.hobbies || [],
        skills: actorUser.skills || [],
        specialties: actorUser.specialties || [],
        profilePictureUrl: actorUser.profilePictureUrl || ''
      });
    }
  }, [actorUser]);

  // Fetch user profiles (VoiceActorProfile) from userRoleSlice — same source as /profile page
  useEffect(() => {
    if (actorId) {
      dispatch(fetchUserProfiles(actorId));
    }
  }, [dispatch, actorId]);

  // Also fetch from voiceActorSlice as fallback
  useEffect(() => {
    if (actorId && !actorUser) {
      dispatch(fetchActorUser(actorId));
    }
  }, [dispatch, actorUser, actorId]);

  // Fetch sample characters for production data
  useEffect(() => {
    if (actorId) {
      dispatch(fetchPrimaryPortfolio(actorId));
    }
  }, [dispatch, actorId]);

  useEffect(() => {
    if (primaryPortfolio?.uuid) {
      dispatch(fetchSampleCharacters(primaryPortfolio.uuid));
    }
  }, [dispatch, primaryPortfolio?.uuid]);

  // Fetch actor applications for production data
  useEffect(() => {
    if (actorId) {
      dispatch(fetchActorApplications(actorId));
    }
  }, [dispatch, actorId]);

  // Handle form input changes
  const handleInputChange = (e, { name, value }) => {
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle adding a new phone
  const handleAddPhone = () => {
    if (newPhone.trim()) {
      setFormData({
        ...formData,
        phones: [...formData.phones, newPhone.trim()]
      });
      setNewPhone('');
    }
  };

  // Handle adding a new hobby
  const handleAddHobby = () => {
    if (newHobby.trim()) {
      setFormData({
        ...formData,
        hobbies: [...formData.hobbies, newHobby.trim()]
      });
      setNewHobby('');
    }
  };

  // Handle adding a new skill
  const handleAddSkill = () => {
    if (newSkill.trim()) {
      setFormData({
        ...formData,
        skills: [...formData.skills, newSkill.trim()]
      });
      setNewSkill('');
    }
  };

  // Handle adding a new specialty
  const handleAddSpecialty = () => {
    if (newSpecialty.trim()) {
      setFormData({
        ...formData,
        specialties: [...formData.specialties, newSpecialty.trim()]
      });
      setNewSpecialty('');
    }
  };

  // Handle removing an item from an array
  const handleRemoveItem = (array, index) => {
    const newArray = [...formData[array]];
    newArray.splice(index, 1);
    setFormData({
      ...formData,
      [array]: newArray
    });
  };

  // Handle form submission
  const handleSubmit = () => {
    if (actorUser) {
      dispatch(updateActorUser({
        actorId: actorUser.uuid,
        actorData: formData
      }));
      setIsEditing(false);
    }
  };

  // Render the profile view
  const renderProfileView = () => {
    if (!actorUser) return null;

    return (
      <Segment>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <Header as="h3">Voice Actor Profile</Header>
          <Button primary onClick={() => setIsEditing(true)}>
            <Icon name="edit" />
            Edit Profile
          </Button>
        </div>

        <Grid columns={2}>
          <Grid.Column width={6}>
            <Image
              src={actorUser.profilePictureUrl || portfolioImage}
              size="medium"
              rounded
              centered
            />
            <div style={{ textAlign: 'center', marginTop: '10px' }}>
              <Rating icon='star' defaultRating={actorUser.rating || 0} maxRating={5} disabled />
              <div>{actorUser.reviewCount || 0} reviews</div>
            </div>
          </Grid.Column>
          <Grid.Column width={10}>
            <Header as="h2">{actorUser.firstName} {actorUser.lastName}</Header>
            <p style={{ fontSize: '1.1em', marginBottom: '20px' }}>{actorUser.description}</p>

            <Header as="h4">Contact Information</Header>
            <p>
              {actorUser.street && <span>{actorUser.street}<br /></span>}
              {actorUser.city && actorUser.state && <span>{actorUser.city}, {actorUser.state} {actorUser.zipcode}<br /></span>}
              {actorUser.country && <span>{actorUser.country}<br /></span>}
            </p>

            {actorUser.phones && actorUser.phones.length > 0 && (
              <div>
                <strong>Phone:</strong> {actorUser.phones.join(', ')}
              </div>
            )}

            <Divider />

            <Grid columns={3}>
              {actorUser.skills && actorUser.skills.length > 0 && (
                <Grid.Column>
                  <Header as="h4">Skills</Header>
                  <List>
                    {actorUser.skills.map((skill, index) => (
                      <List.Item key={index}>
                        <Icon name="check" />
                        <List.Content>{skill}</List.Content>
                      </List.Item>
                    ))}
                  </List>
                </Grid.Column>
              )}

              {actorUser.specialties && actorUser.specialties.length > 0 && (
                <Grid.Column>
                  <Header as="h4">Specialties</Header>
                  <List>
                    {actorUser.specialties.map((specialty, index) => (
                      <List.Item key={index}>
                        <Icon name="star" />
                        <List.Content>{specialty}</List.Content>
                      </List.Item>
                    ))}
                  </List>
                </Grid.Column>
              )}

              {actorUser.hobbies && actorUser.hobbies.length > 0 && (
                <Grid.Column>
                  <Header as="h4">Hobbies</Header>
                  <List>
                    {actorUser.hobbies.map((hobby, index) => (
                      <List.Item key={index}>
                        <Icon name="heart" />
                        <List.Content>{hobby}</List.Content>
                      </List.Item>
                    ))}
                  </List>
                </Grid.Column>
              )}
            </Grid>
          </Grid.Column>
        </Grid>
      </Segment>
    );
  };

  // Render the profile edit form
  // const renderProfileEditForm = () => {
  //   return (
  //     <Segment>
  //       <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
  //         <Header as="h3">Edit Voice Actor Profile</Header>
  //         <Button onClick={() => setIsEditing(false)}>
  //           <Icon name="cancel" />
  //           Cancel
  //         </Button>
  //       </div>

  //       <Form onSubmit={handleSubmit}>
  //         <Grid columns={2}>
  //           <Grid.Column>
  //             <Form.Input
  //               label="First Name"
  //               name="firstName"
  //               value={formData.firstName}
  //               onChange={handleInputChange}
  //               required
  //             />
  //             <Form.Input
  //               label="Last Name"
  //               name="lastName"
  //               value={formData.lastName}
  //               onChange={handleInputChange}
  //               required
  //             />
  //             <Form.Field
  //               control={TextArea}
  //               label="Description"
  //               name="description"
  //               value={formData.description}
  //               onChange={handleInputChange}
  //               placeholder="Tell us about yourself as a voice actor..."
  //             />
  //             <Form.Input
  //               label="Profile Picture URL"
  //               name="profilePictureUrl"
  //               value={formData.profilePictureUrl}
  //               onChange={handleInputChange}
  //               placeholder="https://example.com/your-profile-picture.jpg"
  //             />
  //           </Grid.Column>
  //           <Grid.Column>
  //             <Header as="h4">Address</Header>
  //             <Form.Input
  //               label="Street"
  //               name="street"
  //               value={formData.street}
  //               onChange={handleInputChange}
  //             />
  //             <Form.Group widths="equal">
  //               <Form.Input
  //                 label="City"
  //                 name="city"
  //                 value={formData.city}
  //                 onChange={handleInputChange}
  //               />
  //               <Form.Input
  //                 label="State/Province"
  //                 name="state"
  //                 value={formData.state}
  //                 onChange={handleInputChange}
  //               />
  //             </Form.Group>
  //             <Form.Group widths="equal">
  //               <Form.Input
  //                 label="Postal Code"
  //                 name="zipcode"
  //                 value={formData.zipcode}
  //                 onChange={handleInputChange}
  //               />
  //               <Form.Input
  //                 label="Country"
  //                 name="country"
  //                 value={formData.country}
  //                 onChange={handleInputChange}
  //               />
  //             </Form.Group>
  //           </Grid.Column>
  //         </Grid>

  //         <Divider />

  //         <Grid columns={2}>
  //           <Grid.Column>
  //             <Header as="h4">Phone Numbers</Header>
  //             <Form.Group>
  //               <Form.Input
  //                 width={12}
  //                 placeholder="Add a phone number"
  //                 value={newPhone}
  //                 onChange={(e) => setNewPhone(e.target.value)}
  //               />
  //               <Button type="button" onClick={handleAddPhone}>Add</Button>
  //             </Form.Group>
  //             <div style={{ marginTop: '10px' }}>
  //               {formData.phones.map((phone, index) => (
  //                 <Label key={index} style={{ marginBottom: '5px' }}>
  //                   {phone}
  //                   <Icon name="delete" onClick={() => handleRemoveItem('phones', index)} />
  //                 </Label>
  //               ))}
  //             </div>

  //             <Header as="h4">Skills</Header>
  //             <Form.Group>
  //               <Form.Input
  //                 width={12}
  //                 placeholder="Add a skill"
  //                 value={newSkill}
  //                 onChange={(e) => setNewSkill(e.target.value)}
  //               />
  //               <Button type="button" onClick={handleAddSkill}>Add</Button>
  //             </Form.Group>
  //             <div style={{ marginTop: '10px' }}>
  //               {formData.skills.map((skill, index) => (
  //                 <Label key={index} style={{ marginBottom: '5px' }}>
  //                   {skill}
  //                   <Icon name="delete" onClick={() => handleRemoveItem('skills', index)} />
  //                 </Label>
  //               ))}
  //             </div>
  //           </Grid.Column>
  //           <Grid.Column>
  //             <Header as="h4">Specialties</Header>
  //             <Form.Group>
  //               <Form.Input
  //                 width={12}
  //                 placeholder="Add a specialty"
  //                 value={newSpecialty}
  //                 onChange={(e) => setNewSpecialty(e.target.value)}
  //               />
  //               <Button type="button" onClick={handleAddSpecialty}>Add</Button>
  //             </Form.Group>
  //             <div style={{ marginTop: '10px' }}>
  //               {formData.specialties.map((specialty, index) => (
  //                 <Label key={index} style={{ marginBottom: '5px' }}>
  //                   {specialty}
  //                   <Icon name="delete" onClick={() => handleRemoveItem('specialties', index)} />
  //                 </Label>
  //               ))}
  //             </div>

  //             <Header as="h4">Hobbies</Header>
  //             <Form.Group>
  //               <Form.Input
  //                 width={12}
  //                 placeholder="Add a hobby"
  //                 value={newHobby}
  //                 onChange={(e) => setNewHobby(e.target.value)}
  //               />
  //               <Button type="button" onClick={handleAddHobby}>Add</Button>
  //             </Form.Group>
  //             <div style={{ marginTop: '10px' }}>
  //               {formData.hobbies.map((hobby, index) => (
  //                 <Label key={index} style={{ marginBottom: '5px' }}>
  //                   {hobby}
  //                   <Icon name="delete" onClick={() => handleRemoveItem('hobbies', index)} />
  //                 </Label>
  //               ))}
  //             </div>
  //           </Grid.Column>
  //         </Grid>

  //         <Divider />

  //         <div style={{ textAlign: 'right' }}>
  //           <Button primary type="submit">
  //             <Icon name="save" />
  //             Save Changes
  //           </Button>
  //         </div>
  //       </Form>
  //     </Segment>
  //   );
  // };

  // Render voice samples using production sample characters
  const renderVoiceSamples = () => {
    if (sampleLoading?.characters) {
      return (
        <Segment>
          <Message icon>
            <Icon name='circle notched' loading />
            <Message.Content>
              <Message.Header>Loading samples...</Message.Header>
            </Message.Content>
          </Message>
        </Segment>
      );
    }

    return (
      <Segment>
        <Header as="h3">
          Voice Samples
          <Label circular color="blue" size="small" style={{ marginLeft: '10px' }}>
            {sampleCharacters.length}
          </Label>
        </Header>
        {sampleCharacters.length === 0 ? (
          <Message info>
            <Message.Header>No Voice Samples Yet</Message.Header>
            <p>Create sample characters to showcase your voice talent to book creators.</p>
          </Message>
        ) : (
          <Card.Group>
            {sampleCharacters.map(character => (
              <Card key={character.uuid}>
                <Card.Content>
                  <Card.Header>{character.name || 'Unnamed Character'}</Card.Header>
                  <Card.Meta>
                    {character.characterType && <span>{character.characterType}</span>}
                    {character.gender && <span> • {character.gender}</span>}
                  </Card.Meta>
                  <Card.Description>
                    {character.description
                      ? (character.description.length > 100
                        ? `${character.description.substring(0, 100)}...`
                        : character.description)
                      : 'No description'}
                  </Card.Description>
                </Card.Content>
                <Card.Content extra>
                  <div>
                    {character.genre && <Label size="tiny" style={{ marginBottom: '5px' }}>{character.genre}</Label>}
                    {character.characterType && <Label size="tiny" color="blue" style={{ marginBottom: '5px' }}>{character.characterType}</Label>}
                    {character.hasAudio && <Label size="tiny" color="green" style={{ marginBottom: '5px' }}><Icon name="microphone" /> Audio</Label>}
                  </div>
                </Card.Content>
                <Card.Content extra>
                  <Button
                    fluid
                    primary
                    onClick={() => navigate('/dashboard', { state: { menuSelection: 'VOICE-SAMPLE-WORKSHEET' } })}
                  >
                    <Icon name="edit" />
                    Edit in Sample Manager
                  </Button>
                </Card.Content>
              </Card>
            ))}
          </Card.Group>
        )}
        <div style={{ marginTop: '20px', textAlign: 'right' }}>
          <Button
            primary
            onClick={() => navigate('/dashboard', { state: { menuSelection: 'VOICE-SAMPLE-WORKSHEET' } })}
          >
            <Icon name="plus" />
            Manage Samples
          </Button>
        </div>
      </Segment>
    );
  };

  // Render reviews (placeholder - no review data source yet)
  const renderReviews = () => {
    const reviewCount = actorUser?.reviewCount || 0;

    return (
      <Segment>
        <Header as="h3">Reviews</Header>
        {reviewCount === 0 ? (
          <Message info>
            <Message.Header>No Reviews Yet</Message.Header>
            <p>You haven't received any reviews yet. Complete assignments to get feedback from book creators.</p>
          </Message>
        ) : (
          <Message>
            <Message.Header>{reviewCount} Review(s)</Message.Header>
            <p>Review details will be available in a future update.</p>
          </Message>
        )}
      </Segment>
    );
  };

  // Define tabs
  const panes = [
    {
      menuItem: 'Voice Samples',
      render: () => (
        <Tab.Pane>
          {renderVoiceSamples()}
        </Tab.Pane>
      )
    },
    {
      menuItem: 'Reviews',
      render: () => (
        <Tab.Pane>
          {renderReviews()}
        </Tab.Pane>
      )
    }
  ];

  return (
    <div className="landing-page voice-actor-portfolio-landing listContainer">
      <Container style={{ marginTop: "30px" }}>
        <Grid className="">
          <Grid.Row>
            <Grid.Column width={3}>
              <div className="img-with-gradient">
                <Image
                  src={signedProfileImageUrl || portfolioImage}
                  onError={(e) => { e.target.src = portfolioImage; }}
                  style={{ width: '160px', height: '160px', objectFit: 'cover', borderRadius: '50%' }}
                />
              </div>
            </Grid.Column>
            <Grid.Column width={13} textAlign="center">
              <span className="blgListItemTitle">My Portfolio</span>
              <span className="blgListSubTitle">Voice Actor Portfolio</span>
              <p className="landing-subheading-text">
                Manage your voice acting career from this centralized portfolio hub.
                Update your profile, showcase voice samples, track reviews, and discover new opportunities.
                Use the quick actions below to manage your professional presence.
              </p>
              <div style={{ textAlign: 'left', marginTop: '1em' }}>
                <Button
                  as={Link}
                  to="/dashboard"
                  size="large"
                  className="landing-goto-button"
                >
                  Go to Main Dashboard
                </Button>
              </div>
            </Grid.Column>
          </Grid.Row>
        </Grid>
        <Grid className="articleListWidth" style={{ marginTop: "20px", borderTop: "solid 1px #AAAAAA" }}></Grid>
      </Container>

      {status === 'loading' ? (
        <Container style={{ marginTop: '2em', marginBottom: '2em' }}>
          <Message icon>
            <Icon name='circle notched' loading />
            <Message.Content>
              <Message.Header>Just a moment</Message.Header>
              Loading your portfolio...
            </Message.Content>
          </Message>
        </Container>
      ) : status === 'failed' ? (
        <Container style={{ marginTop: '2em', marginBottom: '2em' }}>
          <Message negative>
            <Message.Header>Error loading portfolio</Message.Header>
            <p>{error || 'There was a problem loading your portfolio. Please try again later.'}</p>
          </Message>
        </Container>
      ) : (
        <>
          <Container style={{ marginTop: '2em', marginBottom: '2em' }}>
            <Grid>
              <Grid.Row>
                <Grid.Column width={6}>
                  <Header as="h2">
                    <img src={myProfileLine} alt="" />
                    <Header.Content style={{
                      fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
                      fontWeight: '400',
                      fontSize: '2.5rem'
                    }}>
                      Portfolio Overview
                      <Header.Subheader>Your voice acting career at a glance</Header.Subheader>
                    </Header.Content>
                  </Header>
                </Grid.Column>
                <Grid.Column width={10}>
                  <div>
                    <Statistic.Group widths="five">
                      {statistics.map((stat, index) => {
                        const StatContent = (
                          <>
                            <Statistic.Value>
                              <div style={{
                                fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
                                fontWeight: '300',
                                fontSize: '3.0rem',
                                color: stat.color || undefined
                              }}>
                                <img src={stat.img} alt="" style={{ height: '40px', marginRight: '10px' }} />
                                {stat.value}
                              </div>
                            </Statistic.Value>
                            <Statistic.Label style={{
                              fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
                              fontWeight: '300',
                              fontSize: '1.1rem',
                              lineHeight: '1.3',
                              color: stat.color || undefined
                            }}>
                              {stat.label.split(' ').map((word, i, arr) => (
                                <React.Fragment key={i}>
                                  {word}
                                  {i < arr.length - 1 && <br />}
                                </React.Fragment>
                              ))}
                            </Statistic.Label>
                          </>
                        );

                        if (stat.link) {
                          const statElement = (
                            <Statistic key={index} as={Link} to={stat.link} className="statistic-link">
                              {StatContent}
                            </Statistic>
                          );

                          // Wrap with Popup if profile is incomplete
                          if (missingFields.length > 0) {
                            return (
                              <Popup
                                key={index}
                                trigger={statElement}
                                hoverable
                                position="bottom center"
                                wide
                              >
                                <Popup.Header>
                                  <Icon name="warning sign" color="orange" /> Missing Profile Fields
                                </Popup.Header>
                                <Popup.Content>
                                  <List bulleted size="small">
                                    {missingFields.map((field, i) => (
                                      <List.Item key={i}>{field}</List.Item>
                                    ))}
                                  </List>
                                  <div style={{ fontSize: '0.85em', color: '#888', marginTop: '0.5em' }}>
                                    Click to edit your profile
                                  </div>
                                </Popup.Content>
                              </Popup>
                            );
                          }

                          return statElement;
                        }

                        return (
                          <Statistic key={index}>
                            {StatContent}
                          </Statistic>
                        );
                      })}
                    </Statistic.Group>
                  </div>
                </Grid.Column>
              </Grid.Row>
            </Grid>
            <Divider />

            <Header as="h2" dividing style={{ marginTop: '2em' }}>
              <img src={lightening} alt="" />
              <Header.Content>
                Quick Actions
                <Header.Subheader>Manage your voice acting portfolio</Header.Subheader>
              </Header.Content>
            </Header>

            <Card.Group itemsPerRow={4} stackable>
              {quickActions.map((action, index) => (
                <Card
                  key={index}
                  onClick={action.action}
                  as={action.link ? Link : 'div'}
                  to={action.link ? (action.state ? { pathname: action.link, state: action.state } : action.link) : undefined}
                  raised
                  style={{ cursor: 'pointer' }}
                >
                  <Card.Content>
                    <img
                      src={action.img}
                      alt=""
                      style={{
                        height: '80px',
                        display: 'block',
                        margin: '0 auto 1em',
                        color: '#2c5d8a'
                      }}
                    />
                    <Card.Header textAlign="center">{action.title}</Card.Header>
                    <Card.Description textAlign="center">
                      {action.description}
                    </Card.Description>
                  </Card.Content>
                </Card>
              ))}
            </Card.Group>

            <Grid columns={1} stackable style={{ marginTop: '2em' }}>
              <Grid.Column>
                <Header as="h2" dividing>
                  <img src={microphoneIcon} alt="" style={{ height: '50px' }} />
                  <Header.Content>
                    Your Portfolio Management
                    <Header.Subheader>Complete profile, voice samples, and reviews management</Header.Subheader>
                  </Header.Content>
                </Header>

                <div ref={tabsRef}>
                  <Tab
                    panes={panes}
                    activeIndex={activeTabIndex}
                    onTabChange={(e, { activeIndex }) => setActiveTabIndex(activeIndex)}
                  />
                </div>
              </Grid.Column>
            </Grid>

            <Grid columns={2} stackable style={{ marginTop: '2em' }}>
              <Grid.Column>
                <Header as="h2" dividing>
                  <img src={profileIcon} alt="" style={{ height: '50px' }} />
                  <Header.Content>
                    Professional Tips
                    <Header.Subheader>Maximize your voice acting success</Header.Subheader>
                  </Header.Content>
                </Header>

                <Segment>
                  <div style={{ marginBottom: '1em' }}>
                    <Header as="h4">
                      <Icon name="lightbulb" color="yellow" />
                      <Header.Content>Portfolio Best Practices</Header.Content>
                    </Header>
                    <p>• Keep your profile complete and up-to-date</p>
                    <p>• Upload diverse voice samples showcasing your range</p>
                    <p>• Respond promptly to client inquiries and feedback</p>
                    <p>• Maintain professional communication in all interactions</p>
                  </div>
                  <div style={{ textAlign: 'center', marginTop: '1em' }}>
                    <Button
                      as={Link}
                      to="/profile"
                      compact
                      primary
                    >
                      Edit Your Profile
                    </Button>
                  </div>
                </Segment>
              </Grid.Column>

              <Grid.Column>
                <Header as="h2" dividing>
                  <img src={lightening} alt="" style={{ height: '50px' }} />
                  <Header.Content>
                    Next Steps
                    <Header.Subheader>Grow your voice acting career</Header.Subheader>
                  </Header.Content>
                </Header>

                <Segment>
                  <div style={{ marginBottom: '1em' }}>
                    <Header as="h4">
                      <Icon name="rocket" color="blue" />
                      <Header.Content>Career Growth</Header.Content>
                    </Header>
                    <p>• Browse available voice acting opportunities</p>
                    <p>• Apply for projects that match your skills</p>
                    <p>• Build relationships with book creators</p>
                    <p>• Continuously improve your craft and equipment</p>
                  </div>
                  <div style={{ textAlign: 'center', marginTop: '1em' }}>
                    <Button
                      as={Link}
                      to="/opportunities"
                      compact
                      color="blue"
                    >
                      Browse Opportunities
                    </Button>
                  </div>
                </Segment>
              </Grid.Column>
            </Grid>
          </Container>
        </>
      )}

      {AuthStdFooter()}
    </div>
  );
};

export default MyPortfolio;
